import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Body3D } from "../components/Body3D";
import { BodyControls } from "../components/BodyControls";
import { ZonePanel, ZoneHint, BackToBody } from "../components/ZonePanel";
import { DoctorChat } from "../components/DoctorChat";
import { ChatWindow } from "../components/ChatWindow";
import { TopBar } from "../components/TopBar";
import { DoctorFab } from "../components/DoctorFab";
import { AdModal } from "../components/AdModal";
import { Disclaimer } from "../components/Disclaimer";
import { CookieBanner } from "../components/CookieBanner";
import { LegalLinks } from "../components/LegalLinks";
import { SafeBoundary } from "../components/SafeBoundary";
import { Splash } from "../components/Splash";
import { InstallButton } from "../components/InstallButton";
import { SkinToggle, SkinPanel } from "../components/SkinMode";
import { makeT, zoneName, subName } from "../lib/i18n";
import { createConsultation, getConsultation, streamPost, saveHistoryItem, markAdStarted, isRateLimited } from "../lib/api";

const SUPPORTED = ["it", "en", "es", "fr", "de"];
const initialLang = () => {
  const saved = localStorage.getItem("drnova_lang");
  const nav = (navigator.language || "it").slice(0, 2);
  return saved || (SUPPORTED.includes(nav) ? nav : "it");
};

const LAYERS = { muscles: true, bones: true, vessels: true, organs: true };

export default function Home() {
  const [lang, setLangState] = useState(initialLang);
  const model = "gpt";
  const [sex, setSex] = useState("female");
  const [age, setAge] = useState(35);
  const [view] = useState("front");
  const [selection, setSelection] = useState(null);
  const [subzone, setSubzone] = useState(null);
  const [consult, setConsult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [ready, setReady] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [adOpen, setAdOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const t = useMemo(() => makeT(lang), [lang]);
  const setLang = (l) => { localStorage.setItem("drnova_lang", l); setLangState(l); };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const pending = useRef("");
  const flushTimer = useRef(null);
  const flush = () => {
    flushTimer.current = null;
    const delta = pending.current;
    pending.current = "";
    if (delta) setMessages((ms) => {
      const copy = [...ms];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta };
      return copy;
    });
  };
  const appendAssistant = (delta) => {
    pending.current += delta;
    if (!flushTimer.current) flushTimer.current = setTimeout(flush, 80);
  };

  const fail = (e) => toast.error(t(isRateLimited(e) ? "rateLimited" : "error"));

  const run = async (path, body, userMsg) => {
    setStreaming(true);
    setMessages((ms) => [...ms, ...(userMsg ? [userMsg] : []), { role: "assistant", content: "" }]);
    try {
      const res = await streamPost(path, body, appendAssistant);
      clearTimeout(flushTimer.current); flush();
      if (res.text) setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? { ...m, content: res.text } : m)));
      if (res.ready) setReady(true);
    } catch (e) {
      clearTimeout(flushTimer.current); flush();
      fail(e);
      setMessages((ms) => (ms[ms.length - 1]?.content === "" ? ms.slice(0, -1) : ms));
    }
    setStreaming(false);
  };

  const [skinMode, setSkinMode] = useState(false);
  const [skinSpot, setSkinSpot] = useState(null);
  const pick = useCallback((sel) => {
    if (skinMode) return setSkinSpot(sel);
    setSelection(sel); setSubzone(null);
  }, [skinMode]);
  const toggleSkin = () => { setSkinMode((v) => !v); setSkinSpot(null); setSelection(null); setSubzone(null); };

  const start = async (general, skinIssue) => {
    const payload = skinIssue
      ? { sex, age, zone: skinSpot.zone, subzone: "skin", language: lang, model, zone_label: zoneName(lang, skinSpot.zone, skinSpot.side), subzone_label: t(`skin_${skinIssue}`), skin_issue: skinIssue }
      : general
      ? { sex, age, zone: "general", subzone: "general", language: lang, model, zone_label: zoneName(lang, "general"), subzone_label: subName(lang, "general") }
      : { sex, age, zone: selection.zone, subzone, language: lang, model, zone_label: zoneName(lang, selection.zone, selection.side), subzone_label: subName(lang, subzone) };
    try {
      const c = await createConsultation(payload);
      setConsult(c); setMessages([]); setReady(false); setDiagnosis(null); setChatOpen(true); setSkinMode(false); setSkinSpot(null);
      saveHistoryItem({ id: c.id, zone_label: c.zone_label, subzone_label: c.subzone_label, date: c.created_at, done: false });
      await run(`/consultations/${c.id}/start`);
    } catch (e) { fail(e); }
  };

  const runDiagnosis = async () => {
    setAdOpen(false);
    setDiagnosis("");
    setStreaming(true);
    let full = "";
    let last = 0;
    try {
      const res = await streamPost(`/consultations/${consult.id}/diagnosis`, null, (d) => {
        full += d;
        const now = Date.now();
        if (now - last > 100) { last = now; setDiagnosis(full); }
      });
      setDiagnosis(res.text || full);
      saveHistoryItem({ id: consult.id, zone_label: consult.zone_label, subzone_label: consult.subzone_label, date: consult.created_at, done: true });
    } catch (e) { fail(e); setDiagnosis(null); }
    setStreaming(false);
  };

  const openItem = async (id) => {
    try {
      const c = await getConsultation(id);
      setConsult(c);
      setMessages(c.messages);
      setDiagnosis(c.diagnosis || null);
      setReady(c.messages.some((m) => m.content.includes("[READY]")));
      setSelection(null);
      setChatOpen(true);
    } catch (e) { toast.error(t("error")); }
  };

  const reset = () => { setConsult(null); setMessages([]); setDiagnosis(null); setReady(false); setSelection(null); setSubzone(null); setChatOpen(false); };
  const canPick = !consult || consult.zone === "general";
  const answers = messages.filter((m) => m.role === "user" && !m.hidden).length;

  return (
    <div className="stage relative h-[100dvh] w-full overflow-hidden" data-testid="home-page">
      <div className="clinic-bg" aria-hidden />
      <div className="absolute inset-0" data-testid="body-viewer">
        <SafeBoundary fallback={<p className="grid h-full place-items-center px-6 text-center text-sm text-slate-600" data-testid="body-3d-error">{t("error")}</p>}><Body3D paused={chatOpen} hideCallouts={chatOpen} sex={sex} age={age} view={view} layers={LAYERS} selection={selection} onPick={pick} lang={lang} subzone={subzone} setSubzone={setSubzone} marker={skinMode ? skinSpot : null} /></SafeBoundary>
      </div>
      {!selection && !chatOpen && (
        <div className="fade-up pointer-events-none absolute inset-x-0 top-[64px] z-10 px-14 text-center sm:px-4 sm:top-[78px]" data-testid="tap-to-analyze-hint">
          <p className="font-display text-lg font-bold leading-tight text-slate-900 drop-shadow-[0_1px_6px_rgba(255,255,255,0.9)] sm:text-2xl" data-testid="tap-to-analyze-title">{t(skinMode ? "skinTitle" : "tapToAnalyzeTitle")}</p>
          <p className={`mt-0.5 text-[13px] font-medium drop-shadow-[0_1px_4px_rgba(255,255,255,0.9)] sm:text-sm ${skinMode ? "text-orange-700" : "text-teal-800"}`}>{t(skinMode ? "skinHint" : "tapToAnalyze")}</p>
        </div>
      )}
      <TopBar t={t} lang={lang} setLang={setLang} onOpenItem={openItem} />
      <DoctorFab t={t} active={!!consult} showBubble={!selection && !chatOpen && !skinSpot}
        onClick={() => { if (chatOpen) return setChatOpen(false); setChatOpen(true); if (!consult && !streaming) start(true); }} />
      <BodyControls t={t} sex={sex} setSex={(s) => { setSex(s); setSelection(null); }} age={age} setAge={setAge} />
      {!chatOpen && !selection && <SkinToggle t={t} active={skinMode} onToggle={toggleSkin} />}
      {skinMode && skinSpot && !chatOpen && (
        <SkinPanel key={skinSpot.key} t={t} lang={lang} spot={skinSpot} loading={streaming} onClose={() => setSkinSpot(null)} onStart={(issue) => start(false, issue)} />
      )}
      {selection && canPick && !subzone && <ZoneHint t={t} lang={lang} selection={selection} onClose={() => setSelection(null)} />}
      {selection && canPick && subzone && (
        <ZonePanel t={t} lang={lang} selection={selection} subzone={subzone} onStart={() => start(false)} onClose={() => setSubzone(null)} loading={streaming} />
      )}
      {selection && canPick && subzone && <BackToBody t={t} onClick={() => { setSubzone(null); setSelection(null); }} />}
      <ChatWindow t={t} open={chatOpen} onClose={() => setChatOpen(false)}>
        {consult && (
          <DoctorChat t={t} consult={consult} messages={messages} streaming={streaming} diagnosis={diagnosis}
            canDiagnose={ready || answers >= 4} onSend={(v, photos) => run(`/consultations/${consult.id}/message`, { text: v, model, photo_ids: photos }, { role: "user", content: v, photos })}
            onDiagnose={() => { markAdStarted(consult.id).then(() => setAdOpen(true)).catch(fail); }} onReset={reset} />
        )}
        {!consult && <p className="animate-pulse pt-2 text-sm text-teal-700" data-testid="chat-loading">{t("typing")}</p>}
      </ChatWindow>
      <AdModal t={t} open={adOpen} onDone={runDiagnosis} />
      <Disclaimer t={t} />
      {!chatOpen && !subzone && <LegalLinks lang={lang} />}
      {!chatOpen && !selection && !skinSpot && <InstallButton t={t} />}
      <CookieBanner lang={lang} />
      <Splash t={t} />
    </div>
  );
}
